import {
  type UndecoratedFinancialReport,
  UndecoratedInvestment,
} from '@flemingmetrics/api-client';
import { sumByKey } from '@/utils/arrays';

export function getLatestReport(
  financialReports: UndecoratedFinancialReport[]
) {
  return financialReports.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    if ((a.month ?? 0) !== (b.month ?? 0)) {
      return (b.month ?? 0) - (a.month ?? 0);
    }
    return (b.quarter ?? 0) - (a.quarter ?? 0);
  })[0];
}

export function sumInvestments(
  investments: { shareCount: number; price: number }[],
  latestReport: UndecoratedFinancialReport | undefined
) {
  const current = parseValuation(latestReport);
  const shares = sumByKey(investments, 'shareCount');
  const totalInvested = sumByKey(investments, 'price');
  const sharePrice = shares
    ? parsePricePerShare(shares, totalInvested.toString())
    : 0;
  const currentValue = shares * current.sharePrice;
  return {
    sharePrice,
    shares,
    totalInvested,
    currentSharePrice: current.sharePrice,
    currentValue: shares * current.sharePrice,
    profitLoss: {
      currency: latestReport?.currency || 'EUR',
      deltaPrice: currentValue - totalInvested,
      deltaPercent: (current.sharePrice - sharePrice) / sharePrice,
    },
  };
}

function parseValuation(report: UndecoratedFinancialReport | undefined) {
  let result = { sharePrice: 0, shares: 0, value: 0 };
  if (!report) return result;
  if (report.totalShareCount) result.shares = report.totalShareCount;
  if (report.valuation) {
    result.value = parseFloat(report.valuation);
    if (report.totalShareCount) {
      result.sharePrice = parsePricePerShare(
        report.totalShareCount,
        report.valuation
      );
    }
  }
  return result;
}

export function parseSharePrice(investment: UndecoratedInvestment) {
  return parsePricePerShare(investment.shareCount, investment.price);
}

export function parseValuationSharePrice(report: UndecoratedFinancialReport) {
  if (!report.valuation || !report.totalShareCount) return 0;
  return parsePricePerShare(report.totalShareCount, report.valuation);
}

function parsePricePerShare(shareCount: number, price: string) {
  // TODO: Should `.toFixed()` be removed? Shares might not be exact 0.xx cents
  // return parseFloat((parseFloat(price) / shareCount).toFixed(2));
  return parseFloat(price) / shareCount;
}
